"use strict";
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spread = (this && this.__spread) || function () {
    for (var ar = [], i = 0; i < arguments.length; i++) ar = ar.concat(__read(arguments[i]));
    return ar;
};
Object.defineProperty(exports, "__esModule", { value: true });
var facadeClients_1 = require("./facadeClients");
var functions_1 = require("../functions");
/**
 * ClientFactory decorator that uses another ClientFactory to build a client, and then updates a MethodKeyCache with
 * all of the methods from the created client.
 *
 * @param decorated The ClientFactory to use to actually construct clients
 * @param methodKeyCache The MethodKeyCache to update with the generated clients methods
 */
function methodKeyCacheClientFactory(state, decorated) {
    return function (apiName, config) {
        var client = decorated(apiName, config);
        var methodNames = Object.getOwnPropertyNames(Object.getPrototypeOf(client));
        methodNames.forEach(function (methodName) {
            if (methodName !== "constructor") {
                // Because consumers pass a reference to the class method, bind it so we use the
                // correct 'this'
                var boundMethod = client[methodName].bind(client);
                client[methodName] = boundMethod;
                // Cache the method string for this state method
                functions_1.setMethodKey(boundMethod, apiName + "." + methodName + ":state(" + JSON.stringify(state) + ")");
            }
        });
        return client;
    };
}
function createFacadeClientsWithCacheEntries(config) {
    var result = facadeClients_1.default(config);
    Object.keys(result).forEach(function (apiName) {
        var facadeClient = result[apiName];
        Object.getOwnPropertyNames(facadeClient).forEach(function (method) {
            if (method === "bindClient") {
                return;
            }
            var facadeMethod = facadeClient[method];
            functions_1.setMethodKey(facadeMethod, function () {
                return functions_1.getMethodKey(facadeMethod.__actualMethod);
            });
        });
    });
    return result;
}
function bindAllClients(facadeClients, actualClients) {
    Object.keys(actualClients).forEach(function (apiName) {
        if (facadeClients[apiName]) {
            facadeClients[apiName].bindClient(actualClients[apiName]);
        }
        else {
            console.warn("Cannot bind client that was not part of initial configuration");
        }
    });
}
/**
 * Create managed clients, with some extra state (e.g. region that is associated with the client).  Instead of
 * the initializer returning a factory, it must return an object with initial state, and a function that returns
 * a factory when given state.
 *
 * Clients are cached when the state changes.  This function returns an array containing (in the following order):
 *  [0] - The map of clients
 *  [1] - The initialize function (that takes the same arguments as the initializer function passed as a param)
 *  [2] - A setter function that sets the current state for the managed clients to something
 *  [3] - A function that allows using clients with a specific state value, without having to set the current state.
 *
 * This is not currently exported externally because the type inference for the initializer and return value isn't
 * currently supported as of typescript 3.7.  Additionally, the vast majority of use cases will be for regional clients
 * which we've special cased and exported.
 *
 * @param clientConfig The client configs to manage
 * @param initializer
 *    The initializer used to initialize the managed clients.  Must return an object containing the initial state, as
 *    well as a function that returns a client factory given state
 */
function createManagedClientsWithState(clientConfig, initializer) {
    var facadeClients = createFacadeClientsWithCacheEntries(clientConfig);
    var createClientsRef = {
        createClients: undefined,
    };
    var clientCache = {};
    var withState = function (state) {
        if (!createClientsRef.createClients) {
            throw Error("Must initialize clients before using them");
        }
        var stateStr = JSON.stringify(state);
        var result = clientCache[stateStr];
        if (!result) {
            var clientFactory_1 = createClientsRef.createClients(state);
            result = Object.keys(clientConfig).reduce(function (acc, apiName) {
                var config = clientConfig[apiName];
                acc[apiName] = clientFactory_1(apiName, config);
                return acc;
            }, {});
            clientCache[stateStr] = result;
        }
        return result;
    };
    var setState = function (state) { return bindAllClients(facadeClients, withState(state)); };
    var init = function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        var _a = initializer.apply(void 0, __spread(args)), initialState = _a.initialState, createClients = _a.createClients;
        createClientsRef.createClients = function (state) {
            return methodKeyCacheClientFactory(state, createClients(state));
        };
        setState(initialState);
    };
    return [
        facadeClients,
        init,
        setState,
        withState,
    ];
}
exports.default = createManagedClientsWithState;
//# sourceMappingURL=createManagedClientsWithState.js.map